import React, { useState, useEffect } from "react";
import { AppLayout } from "../components/layout";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  Eye,
  FileText,
  AlertCircle,
} from "lucide-react";
import { getArticles } from "../services/api";
import type { Article } from "../types";
import { EmptyState } from "@/components/ui/empty-state";
import { useNavigateWithLock } from "../hooks/useNavigateWithLock";

const ArticlesPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigateWithLock();

  const breadcrumbs = [
    { title: "Dashboard", href: "dashboard" },
    { title: "Articles", isCurrentPage: true },
  ];

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    console.log("fetchArticles");
    try {
      setLoading(true);
      setError(null);
      const response = await getArticles();

      if (response.ok && response.articles) {
        setArticles(response.articles);
      } else {
        setError(response.error || "Failed to fetch articles");
      }
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError("Failed to fetch articles");
    } finally {
      setLoading(false);
    }
  };

  // Articles are now managed through help centers
  // This page is read-only for viewing all articles

  const handleViewArticle = (articleSlug: string) => {
    // Articles are viewed through help centers
    // This is a read-only list view
    console.log("View article:", articleSlug);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="flex items-center space-x-4 p-4 border rounded-lg"
              >
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            ))}
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-xl font-bold text-foreground">Articles</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              View all articles in your organization. To create or edit articles, use Help Centers.
            </p>
          </div>
          <Button
            onClick={() => navigate('/help-centers')}
            variant="outline"
            className="flex items-center gap-2 border-primary"
          >
            <Plus className="h-4 w-4" />
            Go to Help Centers
          </Button>
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-destructive mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Articles Table */}
        {articles.length === 0 && !error ? (
          <EmptyState
            title="No articles yet"
            description="Create articles through Help Centers to organize your content."
            icons={[FileText, FileText, FileText]}
            className="mx-auto"
            action={{
              label: "Go to Help Centers",
              onClick: () => navigate('/help-centers'),
            }}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Title
                  </th>
                  {/* <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Description
                  </th> */}
                  {/* <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Author
                  </th> */}
                  <th className="text-left py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Updated
                  </th>
                  {/* Status column removed - articles don't have their own visibility anymore */}
                  <th className="text-right py-1.5 px-2 font-medium text-sm text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => (
                  <tr
                    key={article.id}
                    className="border-b hover:bg-muted/50 transition-colors"
                  >
                    <td className="py-2 px-2">
                      <div className="font-medium text-foreground text-sm">
                        {article.title}
                      </div>
                    </td>
                    {/* <td className="py-4 px-4">
                      <div className="text-sm text-muted-foreground max-w-xs">
                        {truncateText(
                          article.description || "No description available",
                          80
                        )}
                      </div>
                    </td> */}
                    {/* <td className="py-4 px-4">
                      <div className="text-sm text-muted-foreground">
                        {article.author || "—"}
                      </div>
                    </td> */}
                    <td className="py-2 px-2">
                      <div className="text-sm text-muted-foreground">
                        {formatDate(article.updated_at)}
                      </div>
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewArticle(article.slug)}
                          className="h-8 w-8 p-0"
                          title="View article"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ArticlesPage;
